import apiClient from "../services/apiClient";
import { getGraphPngDataUrl } from "../components/shared/exportGraph";
import { getTesterProfile } from "../helpers/testerAccess";

const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";

const safeStringify = (obj) => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

// Convert a base64 data URL to a File. Used to turn the captured graph PNG
// (rendered as a data URL by exportGraph) into a real File for attachment.
const dataUrlToFile = (dataUrl, filename) => {
  const [meta, b64] = dataUrl.split(",");
  const mime = (meta.match(/data:(.*?);base64/) || [, "application/octet-stream"])[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
  return new File([arr], filename, { type: mime });
};

const buildBody = ({ params }) => {
  const lines = [];
  lines.push(`Name: ${params.from_name}`);
  lines.push(`Email: ${params.from_email}`);
  if (params.organization) lines.push(`Organization: ${params.organization}`);
  if (params.country) lines.push(`Country: ${params.country}`);
  lines.push(`Screen: ${params.screen_label}`);
  if (params.rating) lines.push(`Rating: ${params.rating} / 5`);
  lines.push(`App version: ${params.app_version}`);
  lines.push("");
  lines.push("Description");
  lines.push("-----------");
  lines.push(params.description);
  lines.push("");
  lines.push("Inputs (JSON)");
  lines.push("-------------");
  lines.push(params.inputs_json);
  lines.push("");
  lines.push(`User attachments: ${params.user_attachment_info}`);
  return lines.join("\n");
};

/**
 * Send a feedback email via the Gmail API using the dedicated sender Gmail
 * (xlog3dproject@gmail.com). The graph PNG and the user's optional uploads
 * travel as real MIME attachments. The user's typed email is set as Reply-To
 * so the admin can reply directly from their inbox.
 */
export const sendFeedback = async ({
  name,
  email,
  description,
  screenLabel,
  inputs,
  attachments = [],
  attachGraph = true,
  rating,
}) => {
  const profile = getTesterProfile();
  let graphFile = null;
  if (attachGraph) {
    const graphDataUrl = await getGraphPngDataUrl();
    graphFile = dataUrlToFile(graphDataUrl, `graph-${Date.now()}.png`);
  }

  const userAttachmentInfo = attachments.length
    ? attachments
        .map(
          (f) =>
            `${f.name} (${Math.round(f.size / 1024)} KB, ${f.type || "unknown type"})`
        )
        .join("; ")
    : "(none)";

  const params = {
    from_name: name,
    from_email: email,
    organization: profile ? profile.organization || "" : "",
    country: profile ? profile.country || "" : "",
    screen_label: screenLabel || "(unknown screen)",
    description: description || "(no description provided)",
    inputs_json: inputs ? safeStringify(inputs) : "(no inputs captured)",
    user_attachment_info: userAttachmentInfo,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    app_version: APP_VERSION,
    unlocked_at: profile
      ? new Date(Number(localStorage.getItem("xlog3d_tester_unlocked"))).toISOString()
      : "",
    submitted_at: new Date().toISOString(),
    rating: rating || 0,
  };

  const body = buildBody({ params });

  const mimeAttachments = [
    graphFile ? { file: graphFile, filename: graphFile.name } : null,
    ...attachments.map((f) => ({ file: f, filename: f.name })),
  ].filter(Boolean);

  const userBytes = attachments.reduce((sum, f) => sum + f.size, 0);
  const totalAttachmentKb = Math.round(
    ((graphFile ? graphFile.size : 0) + userBytes) / 1024
  );
  // eslint-disable-next-line no-console
  console.log(
    `[feedback] attachments total ≈ ${totalAttachmentKb} KB (${graphFile ? `graph ${Math.round(graphFile.size / 1024)} KB` : "no graph"}${attachments.length ? `, user ${Math.round(userBytes / 1024)} KB in ${attachments.length} file(s)` : ""})`
  );

  try {
    // Sent via our API rather than the Gmail API directly: doing it in the
    // browser required shipping the OAuth client secret and refresh token in
    // the bundle, where anyone could read them.
    const res = await apiClient.post("/feedback", {
      subject: `[XLog3D Feedback] ${screenLabel || "(unknown screen)"} — ${name}`,
      body,
      replyTo: email,
      attachments: mimeAttachments
        .filter((a) => a && a.file)
        .map((a) => ({ name: a.filename, size: a.file.size })),
    });
    return { ok: true, id: (res.data && res.data.data && res.data.data.sent) || true };
  } catch (raw) {
    // eslint-disable-next-line no-console
    console.error("[feedback] send failed:", raw);
    throw raw instanceof Error ? raw : new Error(String(raw));
  }
};
